/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { formatDate } from '@angular/common';
import {
  v3CardDeadlineState,
  v3CardScheduleDate,
  v3ContentCardSchedule,
} from '@app/app/shared/components-v3/content-card/v3-content-card-schedule';
import {
  V3ContentCardItem,
  V3ContentCardMetaEntry,
  V3ContentCardRow,
  V3ContentCardStatusBadge,
  V3ContentCardView,
  v3ContentCardGradient,
  v3ContentCardReservedRows,
} from '@app/app/shared/components-v3/content-card/v3-content-card.model';
import { contentCardTypeLabel } from '@app/app/shared/helpers/content-card-type-label.helper';
import { ContentHelper } from '@app/app/shared/helpers/content-helper';
import { joinCardView, joinDates } from '@app/app/shared/helpers/join-state.helper';
import { AssignmentItemBadge, JoinStateDetails, PlaylistCardShort } from '@app/app/shared/models';
import { isPlaylistItem } from '@app/app/shared/services/join/content-join.service';
import {
  LucideBookOpen,
  LucideCalendar,
  LucideCalendarDays,
  LucideCheck,
  LucideClipboardCheck,
  LucideClock,
  LucideIconInput,
  LucideLayers,
  LucideLock,
  LucideSparkles,
} from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';

export function mapPlaylistCardToContentCardItem(
  card: PlaylistCardShort,
  cardIndex: number,
  transloco: TranslocoService,
): V3ContentCardItem {
  if (isAssignmentCard(card)) {
    return mapAssignmentCardToContentCardItem(card, cardIndex, transloco);
  }
  if (isEventCard(card)) {
    return withJoin(mapEventCardToContentCardItem(card, cardIndex, transloco), card, transloco);
  }

  // Programmes, courses and cards read as one thing: a title over a progress bar. Completion is 100% on the
  // bar rather than a badge, and an LTI score lands on the same bar, so nothing else competes for the row.
  const completed = isPlaylistCardCompleted(card);
  const progress = completed ? 100 : playlistCardProgressPercentage(card);
  const started = !completed && (card.started || progress > 0);
  return withJoin(
    {
      uid: card._id,
      title: card.header?.trim() || playlistCardUntitledLabel(card, transloco),
      titleIsPlaceholder: !card.header?.trim(),
      thumbnail: card.image || null,
      gradient: v3ContentCardGradient(card._id, cardIndex),
      coverIcon: !completed && !started ? LucideLock : null,
      typeLabel: playlistCardTypeLabel(card, transloco),
      typeIcon: playlistCardTypeIcon(card),
      progress,
      overdueBadge: overdueBadge(card.deadline, completed, transloco),
    },
    card,
    transloco,
  );
}

/**
 * Until the learner is enrolled, the join state takes the badge and the progress row. Only playlists are joined on
 * their own: other cards come with the playlist they're in, and events have their own registration, which accepting an
 * invitation to one registers for. A learner with progress is already in, whatever the state says of content they
 * started before it could be joined.
 */
function withJoin(item: V3ContentCardItem, card: PlaylistCardShort, transloco: TranslocoService): V3ContentCardItem {
  const joinable = isPlaylistItem(card) || (isEventCard(card) && card.joinState?.state === 'INVITED');
  if (!joinable || hasProgress(card)) {
    return item;
  }
  return withJoinState(item, card.joinState, transloco);
}

/** Puts the learner's join state on a joinable card: its badge, and the join row in place of the progress row. */
export function withJoinState(
  item: V3ContentCardItem,
  joinState: JoinStateDetails | null | undefined,
  transloco: TranslocoService,
): V3ContentCardItem {
  const view = joinCardView(joinState, joinDates(transloco.getActiveLang()));
  if (!view) {
    return item;
  }
  return {
    ...item,
    progress: null,
    statusBadge: view.badge ? { label: transloco.translate(view.badge.key, view.badge.params), tone: view.badge.tone } : item.statusBadge,
    join: {
      caption: transloco.translate(view.caption.key, view.caption.params),
      button: view.button ? { label: transloco.translate(view.button.labelKey), primary: view.button.primary } : null,
    },
  };
}

/** A deadline that has passed is the one date these cards still flag, in the cover's corner. */
function overdueBadge(
  deadline: Date | string | null | undefined,
  completed: boolean,
  transloco: TranslocoService,
): V3ContentCardMetaEntry | null {
  if (completed || !deadline || v3CardDeadlineState(deadline) !== 'overdue') {
    return null;
  }
  const date = v3CardScheduleDate(new Date(deadline), transloco);
  return { icon: LucideClock, text: transloco.translate('translations.cardItem.schedule.overdueOn', { date }) };
}

/** Events keep their dates and completion chip — the schedule is the point of the card. */
function mapEventCardToContentCardItem(card: PlaylistCardShort, cardIndex: number, transloco: TranslocoService): V3ContentCardItem {
  const completed = isPlaylistCardCompleted(card);
  const schedule = v3ContentCardSchedule({ deadline: card.deadline, eventStart: card.eventTime?.start, completed }, transloco);
  return {
    uid: card._id,
    title: card.header?.trim() || playlistCardUntitledLabel(card, transloco),
    titleIsPlaceholder: !card.header?.trim(),
    thumbnail: card.image || null,
    gradient: v3ContentCardGradient(card._id, cardIndex),
    coverIcon: null,
    typeLabel: playlistCardTypeLabel(card, transloco),
    typeIcon: playlistCardTypeIcon(card),
    statusBadge: completed ? { label: transloco.translate('translations.cardItem.completed'), tone: 'success', icon: LucideCheck } : null,
    coverBadge: schedule.coverBadge,
    metaBadge: schedule.badge,
  };
}

export function playlistCardsReservedRows(
  cards: readonly PlaylistCardShort[],
  transloco: TranslocoService,
  view: V3ContentCardView = 'grid',
): V3ContentCardRow[] {
  return v3ContentCardReservedRows(
    (cards ?? []).map((card, index) => mapPlaylistCardToContentCardItem(card, index, transloco)),
    view,
  );
}

export function openPlaylistCard(card: PlaylistCardShort, emit: (card: PlaylistCardShort) => void): void {
  emit(card);
}

function playlistCardUntitledLabel(card: PlaylistCardShort, transloco: TranslocoService): string {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  switch (type) {
    case 'COURSE':
    case 'LTI_COURSE':
      return transloco.translate('translations.cardItem.untitled.course');
    case 'PROGRAM':
    case 'MODULE':
      return transloco.translate('translations.cardItem.untitled.programme');
    case 'EVENT':
      return transloco.translate('translations.cardItem.untitled.event');
    case 'ASSIGNMENT':
      return transloco.translate('translations.cardItem.untitled.assignment');
    case 'RESOURCE':
      return transloco.translate('translations.cardItem.untitled.resource');
    case 'ASSESSMENT':
      return transloco.translate('translations.cardItem.untitled.assessment');
    default:
      return transloco.translate('translations.cardItem.untitled.card');
  }
}

function playlistCardTypeLabel(card: PlaylistCardShort, transloco: TranslocoService): string {
  const type = card?.v3CardType || card?.collectionType;
  if (type?.toUpperCase() === 'LTI_COURSE') {
    if (card?.lmsPlatformType === 'CANVAS') {
      return transloco.translate('translations.cardItem.type.CANVAS_COURSE');
    }
    return transloco.translate('translations.cardItem.type.LTI_COURSE');
  }
  return contentCardTypeLabel(transloco, card?.v3CardType, card?.collectionType, card?.type);
}

function playlistCardTypeIcon(card: PlaylistCardShort): LucideIconInput | null {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  if (!type) {
    return null;
  }
  if (type === 'EVENT') {
    return LucideCalendar;
  }
  if (type === 'ASSIGNMENT') {
    return LucideClipboardCheck;
  }
  if (type === 'MODULE' || type === 'PROGRAM') {
    return LucideLayers;
  }
  if (type === 'PERSONALITY_INDICATOR') {
    return LucideSparkles;
  }
  return LucideBookOpen;
}

function isAssignmentCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType)?.toUpperCase() === 'ASSIGNMENT' || card?.type === 'ASSIGNMENT';
}

/**
 * Assignment section items: thumbnail/title/blurb as usual, but no LearnerCard progress or lock
 * cover (assignment completion is gradebook-tracked, not card-completion-tracked). The state chip is the
 * viewer-specific badge the course content tab merged in (learner submission state / admin Draft-Published).
 */
function mapAssignmentCardToContentCardItem(card: PlaylistCardShort, cardIndex: number, transloco: TranslocoService): V3ContentCardItem {
  const state = card.assignmentItemState;
  return {
    uid: card._id,
    title: card.header?.trim() || playlistCardUntitledLabel(card, transloco),
    titleIsPlaceholder: !card.header?.trim(),
    // The blurb is the card's authored rich content; `subtitle` is interpolated, so flatten it or the tags show.
    subtitle: ContentHelper.toPlainText(card.blurb) || undefined,
    thumbnail: card.image || null,
    gradient: v3ContentCardGradient(card._id, cardIndex),
    coverIcon: LucideClipboardCheck,
    typeLabel: playlistCardTypeLabel(card, transloco),
    typeIcon: LucideClipboardCheck,
    statusBadge: state ? assignmentStatusBadge(state.badge, transloco) : null,
    meta: assignmentDeadlineMeta(state?.deadline, transloco),
    // Work already handed in or marked is past caring about the deadline.
    overdueBadge: overdueBadge(state?.deadline, !isAwaitingSubmission(state?.badge), transloco),
  };
}

function isAwaitingSubmission(badge: AssignmentItemBadge | undefined): boolean {
  return badge === 'NOT_SUBMITTED' || badge === 'RETURNED';
}

function assignmentStatusBadge(badge: AssignmentItemBadge, transloco: TranslocoService): V3ContentCardStatusBadge {
  switch (badge) {
    case 'DRAFT':
      return { label: transloco.translate('translations.cardItem.status.draft'), tone: 'draft' };
    case 'PUBLISHED':
      return { label: transloco.translate('translations.cardItem.status.published'), tone: 'success' };
    case 'ARCHIVED':
      return { label: transloco.translate('translations.cardItem.status.archived'), tone: 'archived' };
    case 'UNDER_REVIEW':
      return { label: transloco.translate('translations.courses.mySubmissions.status.submitted'), tone: 'neutral' };
    case 'RETURNED':
      return { label: transloco.translate('translations.courses.mySubmissions.status.revisionsRequested'), tone: 'warning' };
    case 'RELEASED':
      return { label: transloco.translate('translations.courses.mySubmissions.status.marked'), tone: 'success', icon: LucideCheck };
    default:
      return { label: transloco.translate('translations.cardItem.notStarted'), tone: 'neutral' };
  }
}

function assignmentDeadlineMeta(deadline: string | null | undefined, transloco: TranslocoService): V3ContentCardMetaEntry[] | undefined {
  if (!deadline) {
    return undefined;
  }
  // 'en-US' matches the app-wide date-pipe output (no LOCALE_ID override is registered).
  const date = formatDate(deadline, 'd MMM', 'en-US');
  return [{ icon: LucideCalendarDays, text: transloco.translate('translations.courses.mySubmissions.due', { date }) }];
}

function isLtiCourseCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType)?.toUpperCase() === 'LTI_COURSE';
}

function isEventCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType || card?.type)?.toUpperCase() === 'EVENT';
}

// For LTI courses the percentage is a grade, not progress — 100% there must not imply completion.
/** Progress as the card shows it: started, items done or completed, whichever the listing reports. */
function hasProgress(card: PlaylistCardShort): boolean {
  return !!card.started || isPlaylistCardCompleted(card) || playlistCardProgressPercentage(card) > 0;
}

function isPlaylistCardCompleted(card: PlaylistCardShort): boolean {
  return !!card?.completed || (!isLtiCourseCard(card) && playlistCardProgressPercentage(card) >= 100);
}

function playlistCardProgressPercentage(card: PlaylistCardShort): number {
  const completed = card?.completedItemsCount ?? 0;
  const total = card?.totalItemsCount ?? 0;
  if (total > 0) {
    return Math.round((completed / total) * 100);
  }
  return card?.completed ? 100 : 0;
}
